/**
 * Perfiles PML del Brain — UN archivo por perfil (cliente · BU).
 *
 * Un perfil es la whitelist de qué entra al modo PML de una cuenta y BU
 * (`/pml rappi rts` en el MCP general, scope `pml:rappi.rts`): reuniones por
 * evento recurrente de Calendar, canales de Slack, etiqueta de Gmail, spaces de
 * Google Chat, lados cliente/Winclap y la config del status automático. Lo que
 * no está listado no entra al modo PML aunque esté indexado para el cliente.
 *
 * Es el hermano de `shared/brain-client-config.ts` (misma maquinaria a propósito:
 * zod, `npm run brain:clients` regenera los dos índices, `test:brain-profiles` en
 * CI, PR del formulario). El filtro de retrieval se arma EN LA CONSULTA a partir
 * del perfil (lib/brain-profile-filter.ts), no como etiqueta en cada página: el
 * content-hash de `upsertBrainPage` incluye `content_detail`, así que re-etiquetar
 * páginas al editar un perfil sería re-embeber todo. Con el filtro en la consulta
 * editar un perfil tiene efecto inmediato y no cuesta nada.
 *
 * Puro (sin I/O): lo usan el generador, el test, el generador de PRs y, a través
 * del índice, backend y frontend (solo la vista PÚBLICA: `public.ts`).
 */
import { z } from 'zod';
import { BRAIN_CLIENT_ID_RE, type BrainClientConfig } from './brain-client-config';

/** `<clientId>.<bu>`: el cliente del registro y un slug corto de la BU. */
export const BRAIN_PROFILE_ID_RE = /^[a-z0-9][a-z0-9-]{1,39}\.[a-z0-9][a-z0-9-]{0,29}$/;
export const BRAIN_PROFILE_BU_RE = /^[a-z0-9][a-z0-9-]{0,29}$/;
/** Única solución con perfiles hoy (Paid Media). Sumar otra = agregarla acá, nunca un string suelto. */
export const BRAIN_PROFILE_SOLUTIONS = ['pml'] as const;
export type BrainProfileSolution = (typeof BRAIN_PROFILE_SOLUTIONS)[number];
export const BRAIN_PROFILE_WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;
export type BrainProfileWeekday = (typeof BRAIN_PROFILE_WEEKDAYS)[number];

/** Prefijo del scope en el MCP general y en la telemetría: `pml:rappi.rts`. */
export const BRAIN_PML_SCOPE_PREFIX = 'pml:';

const nonEmpty = z.string().trim().min(1);
/** Casilla del Workspace de Winclap (la única que puede ser owner, manager, reader o casilla de Gmail de un perfil). */
export const isWinclapEmail = (e: string | null | undefined): boolean => /^[^\s@]+@winclap\.com$/i.test((e ?? '').trim());
const winclapEmail = z
  .string()
  .trim()
  .toLowerCase()
  .email()
  .refine(isWinclapEmail, 'tiene que ser una casilla @winclap.com');
const slackChannelId = z.string().regex(/^[CG][A-Z0-9]{6,}$/, 'id de canal de Slack inválido');
const chatSpace = z.string().regex(/^spaces\/[A-Za-z0-9_-]+$/, 'id de space de Google Chat con la forma spaces/XXXX');
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'fecha YYYY-MM-DD');
/** Etiqueta de Gmail tal como la ve el usuario ("brain/rappi-rts"); anidada con `/`, sin barras en los extremos. */
const gmailLabel = nonEmpty.max(100).refine((l) => !l.startsWith('/') && !l.endsWith('/'), 'la etiqueta no lleva barras en los extremos');

export const brainProfileConfigSchema = z
  .object({
    id: z.string().regex(BRAIN_PROFILE_ID_RE, 'id: <cliente>.<bu> en minúsculas, dígitos y guiones'),
    clientId: z.string().regex(BRAIN_CLIENT_ID_RE, 'clientId: id del registro de clientes'),
    solution: z.enum(BRAIN_PROFILE_SOLUTIONS),
    bu: z.object({ name: nonEmpty.max(60), emoji: z.string().trim().max(8).optional() }).strict(),
    /** PS o lead que mantiene la whitelist. */
    owner: winclapEmail,
    manager: z
      .object({
        name: nonEmpty.max(80),
        email: winclapEmail.optional(),
        /** Canal donde llegan las alertas de cobertura y la copia del status (estilo pml-procesos-pod-<manager>). */
        alertsChannel: slackChannelId.optional(),
      })
      .strict(),
    /** Reuniones por evento RECURRENTE de Calendar (no por nombre ni por carpeta de Drive). */
    meetings: z
      .array(
        z
          .object({
            recurringEventId: nonEmpty.max(200),
            label: nonEmpty.max(120).optional(),
            /** Calendario donde se leen las instancias; por defecto el del owner del perfil. */
            calendarOf: winclapEmail.optional(),
          })
          .strict()
      )
      .default([]),
    slack: z
      .object({
        /** Canales propios de la BU: entran enteros. Tienen que estar en el JSON del cliente. */
        channelIds: z.array(slackChannelId).default([]),
        /** Canales compartidos con otras BU: entra solo lo que nombra la BU (`buFilter`). */
        sharedChannelIds: z.array(slackChannelId).default([]),
        /** Términos que identifican a la BU en los canales compartidos ("RTs", "Rappitenderos"). */
        buFilter: z.array(nonEmpty.max(60)).default([]),
      })
      .strict()
      .default({ channelIds: [], sharedChannelIds: [], buFilter: [] }),
    sides: z
      .object({ client: z.array(nonEmpty.max(80)).default([]), winclap: z.array(nonEmpty.max(80)).default([]) })
      .strict()
      .default({ client: [], winclap: [] }),
    gmail: z
      .object({
        /** Opt-in: solo los hilos con esta etiqueta, en estas casillas. Nada de barrer casillas enteras. */
        label: gmailLabel,
        mailboxes: z.array(winclapEmail).default([]),
      })
      .strict()
      .optional(),
    googleChat: z
      .object({
        spaces: z.array(chatSpace).default([]),
        /** Winclapper miembro de los spaces al que se impersona para leerlos; por defecto el owner. */
        reader: winclapEmail.optional(),
      })
      .strict()
      .optional(),
    /** Clave del cliente en analytics-mcp / media-mcp (la usa el status). */
    analyticsKey: nonEmpty.max(60).optional(),
    status: z
      .object({
        enabled: z.boolean().default(false),
        days: z.array(z.enum(BRAIN_PROFILE_WEEKDAYS)).default([]),
        outputChannel: slackChannelId.optional(),
      })
      .strict()
      .optional(),
    /** Última revisión humana de la whitelist (la cobertura avisa a los 90 días). */
    reviewedAt: isoDate.optional(),
    /** Preguntas sugeridas del modo PML (antes que las genéricas). */
    questions: z
      .object({ es: z.array(nonEmpty), en: z.array(nonEmpty), pt: z.array(nonEmpty) })
      .strict()
      .optional(),
  })
  .strict();

export type BrainProfileConfig = z.infer<typeof brainProfileConfigSchema>;
/** Lo que se escribe en el JSON (los defaults pueden faltar). */
export type BrainProfileConfigInput = z.input<typeof brainProfileConfigSchema>;

/** Parsea y normaliza un JSON de perfil. Tira con el path del campo que falla. */
export function parseBrainProfileConfig(raw: unknown, label = 'perfil'): BrainProfileConfig {
  const r = brainProfileConfigSchema.safeParse(raw);
  if (!r.success) {
    const issues = r.error.issues.map((i) => `${i.path.join('.') || '(raíz)'}: ${i.message}`).join('; ');
    throw new Error(`${label}: ${issues}`);
  }
  return r.data;
}

/**
 * Reglas entre perfiles y contra el registro de clientes. Devuelve la lista de
 * errores (vacía = OK). Lo que acá falla, falla en el form / CI, no en el sync.
 */
export function validateBrainProfileConfigs(
  profiles: BrainProfileConfig[],
  clients: Readonly<Record<string, BrainClientConfig>>
): string[] {
  const errors: string[] = [];
  const seenId = new Set<string>();
  const labelByMailbox = new Map<string, string>(); // `${mailbox}|${label}` → profileId

  for (const p of profiles) {
    if (seenId.has(p.id)) errors.push(`id "${p.id}" repetido`);
    seenId.add(p.id);

    if (!p.id.startsWith(`${p.clientId}.`)) errors.push(`${p.id}: el id tiene que empezar con el clientId ("${p.clientId}.")`);
    const bu = p.id.slice(p.clientId.length + 1);
    if (p.id.startsWith(`${p.clientId}.`) && !BRAIN_PROFILE_BU_RE.test(bu)) errors.push(`${p.id}: slug de BU inválido "${bu}"`);

    const client = Object.prototype.hasOwnProperty.call(clients, p.clientId) ? clients[p.clientId] : undefined;
    if (!client) {
      errors.push(`${p.id}: el cliente "${p.clientId}" no está en shared/brain-clients (onboardealo primero)`);
    } else if (client.pseudo) {
      errors.push(`${p.id}: "${p.clientId}" es un pseudo-cliente, no puede tener perfiles`);
    } else {
      const known = new Set(client.slack.channels.map((c) => c.channelId));
      for (const ch of [...p.slack.channelIds, ...p.slack.sharedChannelIds]) {
        if (!known.has(ch)) {
          errors.push(`${p.id}: el canal ${ch} no está en shared/brain-clients/${p.clientId}.json — agregalo ahí (el sync de Slack indexa por cliente, el perfil solo filtra)`);
        }
      }
    }

    const own = new Set(p.slack.channelIds);
    if (own.size !== p.slack.channelIds.length) errors.push(`${p.id}: canal repetido en slack.channelIds`);
    for (const ch of p.slack.sharedChannelIds) {
      if (own.has(ch)) errors.push(`${p.id}: el canal ${ch} está en channelIds y en sharedChannelIds a la vez`);
    }
    if (p.slack.sharedChannelIds.length && !p.slack.buFilter.length) {
      errors.push(`${p.id}: hay canales compartidos pero slack.buFilter está vacío (no habría forma de quedarse con lo de la BU)`);
    }

    const events = new Set<string>();
    for (const m of p.meetings) {
      if (events.has(m.recurringEventId)) errors.push(`${p.id}: evento de Calendar ${m.recurringEventId} repetido`);
      events.add(m.recurringEventId);
    }

    if (p.gmail) {
      const boxes = new Set<string>();
      for (const mb of p.gmail.mailboxes) {
        if (boxes.has(mb)) errors.push(`${p.id}: casilla ${mb} repetida en gmail.mailboxes`);
        boxes.add(mb);
        const key = `${mb}|${p.gmail.label.toLowerCase()}`;
        const other = labelByMailbox.get(key);
        if (other && other !== p.id) errors.push(`la etiqueta "${p.gmail.label}" de ${mb} está en dos perfiles: ${other} y ${p.id}`);
        labelByMailbox.set(key, p.id);
      }
    }

    if (p.googleChat) {
      const spaces = new Set<string>();
      for (const s of p.googleChat.spaces) {
        if (spaces.has(s)) errors.push(`${p.id}: space ${s} repetido`);
        spaces.add(s);
      }
    }

    if (p.status?.enabled) {
      if (!p.status.outputChannel) errors.push(`${p.id}: status.enabled sin status.outputChannel`);
      if (!p.status.days.length) errors.push(`${p.id}: status.enabled sin status.days`);
    }
  }
  return errors;
}

// ─── Scope del MCP (`pml:<cliente>.<bu>`) ─────────────────────────────────────

export const brainProfileScopeId = (profileId: string): string => `${BRAIN_PML_SCOPE_PREFIX}${profileId}`;

/** El id de perfil de un scope `pml:…`, o null si no tiene esa forma. No consulta ningún registro. */
export function brainProfileIdOfScope(scopeId: string | null | undefined): string | null {
  if (!scopeId || !scopeId.startsWith(BRAIN_PML_SCOPE_PREFIX)) return null;
  const id = scopeId.slice(BRAIN_PML_SCOPE_PREFIX.length);
  return BRAIN_PROFILE_ID_RE.test(id) ? id : null;
}

export const isBrainPmlScope = (scopeId: string | null | undefined): boolean => brainProfileIdOfScope(scopeId) !== null;

// ─── Archivos e índices generados ─────────────────────────────────────────────

export const BRAIN_PROFILES_DIR = 'shared/brain-profiles';
export const BRAIN_PROFILES_INDEX_PATH = `${BRAIN_PROFILES_DIR}/index.ts`;
/** Vista liviana (id, cliente, BU, preguntas): lo ÚNICO del registro que entra al bundle del frontend. */
export const BRAIN_PROFILES_PUBLIC_PATH = `${BRAIN_PROFILES_DIR}/public.ts`;

export interface BrainProfilePublic {
  id: string;
  clientId: string;
  solution: BrainProfileSolution;
  bu: BrainProfileConfig['bu'];
  questions?: BrainProfileConfig['questions'];
}

export function toBrainProfilePublic(p: BrainProfileConfig): BrainProfilePublic {
  return {
    id: p.id,
    clientId: p.clientId,
    solution: p.solution,
    bu: p.bu,
    ...(p.questions ? { questions: p.questions } : {}),
  };
}

/** "Rappi · Rappitenderos (RTs)": el nombre visible de un perfil, dado el nombre del cliente. */
export function brainProfileDisplayName(p: Pick<BrainProfilePublic, 'bu'>, clientName: string): string {
  return `${clientName} · ${p.bu.name}`;
}

/** Ruta del JSON de un perfil dentro del repo. */
export function brainProfileJsonPath(profileId: string): string {
  return `${BRAIN_PROFILES_DIR}/${profileId}.json`;
}

const JSON_KEY_ORDER = [
  'id', 'clientId', 'solution', 'bu', 'owner', 'manager', 'meetings', 'slack', 'sides',
  'gmail', 'googleChat', 'analyticsKey', 'status', 'reviewedAt', 'questions',
];

/** Cómo se escribe un JSON de perfil (orden de claves fijo, 2 espacios, salto final). */
export function renderBrainProfileJson(config: BrainProfileConfigInput): string {
  const src = config as Record<string, unknown>;
  const ordered: Record<string, unknown> = {};
  for (const k of JSON_KEY_ORDER) {
    if (src[k] !== undefined) ordered[k] = src[k];
  }
  return JSON.stringify(ordered, null, 2) + '\n';
}

const renderEntries = (items: Array<{ id: string }>): string =>
  items.map((c) => `  ${JSON.stringify(c.id)}: ${JSON.stringify(c, null, 2).replace(/\n/g, '\n  ')},`).join('\n');

/** El índice generado (completo, solo backend): configs normalizados, ordenados por id. Determinista. */
export function renderBrainProfilesIndex(configs: BrainProfileConfig[]): string {
  const sorted = [...configs].sort((a, b) => a.id.localeCompare(b.id));
  return (
    '// GENERADO por `npm run brain:clients` a partir de shared/brain-profiles/*.json — NO editar a mano.\n' +
    '// Un perfil nuevo = un JSON nuevo + regenerar este índice. `test:brain-profiles` (CI) falla si está viejo.\n' +
    '// SOLO backend y scripts: trae canales, casillas, spaces y eventos. El front importa public.ts.\n' +
    "import type { BrainProfileConfig } from '../brain-profile-config';\n\n" +
    `export const BRAIN_PROFILE_REGISTRY: Readonly<Record<string, BrainProfileConfig>> = {\n${renderEntries(sorted)}\n};\n\n` +
    'export const BRAIN_PROFILE_IDS: readonly string[] = Object.keys(BRAIN_PROFILE_REGISTRY);\n'
  );
}

/** El índice público: sin canales, casillas, spaces ni eventos. El JS de Vite se sirve antes del login. */
export function renderBrainProfilesPublic(configs: BrainProfileConfig[]): string {
  const sorted = [...configs].sort((a, b) => a.id.localeCompare(b.id)).map(toBrainProfilePublic);
  return (
    '// GENERADO por `npm run brain:clients` a partir de shared/brain-profiles/*.json — NO editar a mano.\n' +
    '// Vista PÚBLICA del registro de perfiles (id, cliente, BU, preguntas): es lo único que importa el frontend.\n' +
    '// Lo demás (canales, casillas, spaces, eventos de Calendar) vive en index.ts y NO entra al bundle del front.\n' +
    "import type { BrainProfilePublic } from '../brain-profile-config';\n\n" +
    `export const BRAIN_PROFILE_PUBLIC: Readonly<Record<string, BrainProfilePublic>> = {\n${renderEntries(sorted)}\n};\n`
  );
}
